import { useNavigate, useParams } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Button } from '@mui/material';
import { PageLayout } from '../../PageLayout';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { addDrawer } from '../../../store/Drawer';
import {
  ProvisionalAsset,
  ProvisionalAssetUpdate,
  fetchProvisionalAsset,
  getProvisionalAssetSubject,
  handleCommissionsInfoValidation,
  handleRpmAccountValidation,
  handleValidationStatusValidation,
  updateProvisionalAsset
} from '../../../store/ProvisionalAsset';
import {
  provisionalAssetSectionsInfo,
  commissionsSectionInfo,
  assetValidationInfo,
  assetSectionMeta,
  provisionalAssetRpmAccountsInfo
} from './provisionalAssetHelpers';
import { TicketMiniTable } from '../../Compounds/Ticket/TicketMiniTable';
import { fetchTicketsByPAId } from '../../../store/Ticket';
import { RPMMiniTable } from '../../Compounds/RPM/RPMMiniTable';
import {
  fetchRpmAccountsByPAsset,
  getTallyRpmAccountsByPAssetLoadingSubject,
  getTallyRpmAccountsByPAssetSubject
} from '../../../store/RpmAccount';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { isData, isObserver } from '../../../store/User';
import { DetailsLinkCTA } from '../../Compounds/DetailsLinkCTA';
import { NewLinkDialog } from '../../Compounds/Linking/NewLinkDialog/NewLinkDialog';
import { deleteLink } from '../../../store/Link';
import { EntityType } from '../../../store/Search';
import { DetailViewValidationCard, ValidatorResult } from '../../Compounds/CardWithTitle';
import { formatValidationMessages, handleRunValidator } from '../../Compounds/CardWithTitle/validationCardHelpers';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { getSelectedTab, updateState } from '../../../store/GeneralStore';
import { TabPanel } from '../../Atoms/TabPanel';
import { AllocationMiniTable } from '../../Compounds/Allocation/AllocationMiniTable';
import { AttributionTable } from '../../Compounds/Attribution/AttributionTable';
import { CorrectionMiniTable } from '../../Compounds/Correction/CorrectionTable';
import { SpiffTable } from '../../Compounds/Spiff/SpiffTable';
import {
  fetchCorrections,
  getTallyCorrectionsLoadingSubject,
  getTallyCorrectionsSubject
} from '../../../store/Correction';
import { HistoryMiniTable } from '../../Compounds/History/HistoryTable';
import {
  fetchChangeHistory,
  getChangeHistoryLoadingSubject,
  getChangeHistorySubject
} from '../../../store/ChangeHistory';

const tabs = ['Info', 'Reconcile', 'Allocation', 'History'];

export const ProvisionalAssetDetailPage = () => {
  const { id } = useParams();
  const [data, setData] = useState<ProvisionalAsset>({} as ProvisionalAsset);
  const [link, setLink] = useState('');
  const [tabIndex, setTabIndex] = useState<number>(
    getSelectedTab().provisionalAssetDetail ? Number(getSelectedTab().provisionalAssetDetail) : 0
  );
  const [isLinkFormOpen, setIsLinkFormOpen] = useState<boolean>(false);
  const [validationStatusBySection, setValidationStatusBySection] = useState<Record<string, ValidatorResult>>({});
  const navigate = useNavigate();
  const { setSnack } = useSnackbar();

  const handleTabChange = (tabIndex: number) => {
    setTabIndex(tabIndex);
    updateState({
      selectedTab: {
        ...getSelectedTab(),
        provisionalAssetDetail: tabIndex
      }
    });
  };

  const cardActions = () => {
    return isObserver() || isData() ? <></> : <DetailsLinkCTA setIsFormOpen={() => setIsLinkFormOpen(true)} />;
  };

  const handleBack = () => {
    addDrawer({
      id: v4(),
      data,
      ...getMetaData({ name: ComponentName.PROVISIONAL_ASSET, id: data.id })
    });
    navigate(-1);
  };

  useEffect(() => {
    const assetSub = getProvisionalAssetSubject().subscribe((a) => {
      setData(a);
      setLink(getMetaData({ name: ComponentName.PROVISIONAL_ASSET, id: a.id }).link || '');
    });
    return () => {
      if (assetSub) assetSub.unsubscribe();
    };
  }, []);

  const getData = async () => {
    if (!id) return;

    const errorStatusCode = await fetchProvisionalAsset(id);
    if (errorStatusCode) navigate(`/error/${errorStatusCode}/provisional-asset/${id}/${new Date().getTime()}`);
  };

  useEffect(() => {
    const messages = formatValidationMessages(validationStatusBySection);
    if (messages.length) {
      setSnack({
        message: messages.join(', '),
        type: 'warning',
        duration: 15000,
        open: true
      });
    }
  }, [JSON.stringify(validationStatusBySection)]);

  useEffect(() => {
    getData();
    if (id) fetchProvisionalAsset(id);
  }, [id]);

  const pageActions = () => {
    return (
      <Button
        color="secondary"
        variant="outlined"
        startIcon={<ArrowBackIosIcon />}
        onClick={handleBack}>
        Back
      </Button>
    );
  };

  const tabContent = () => {
    switch (tabIndex) {
      case 0:
        return (
          <div className="grid grid-cols-2 space-x-6">
            <div className="space-y-6">
              <DetailViewValidationCard
                enableCollapse
                key="commissions_detail"
                title="Commissions"
                data={commissionsSectionInfo(data)}
                saveAction={(d) => saveAction(d as ProvisionalAssetUpdate)}
                wrapperClassName="min-h-[15rem]"
                detailValidator={(d) =>
                  handleRunValidator(
                    'commissionsInfo',
                    d,
                    handleCommissionsInfoValidation,
                    validationStatusBySection,
                    setValidationStatusBySection
                  )
                }
              />
              <DetailViewValidationCard
                cardActions={cardActions()}
                enableCollapse
                key="rpm_account_detail"
                title="RPM Accounts"
                data={provisionalAssetRpmAccountsInfo(data)}
                detailValidator={(d) =>
                  handleRunValidator(
                    'rpmAccounts',
                    d,
                    handleRpmAccountValidation,
                    validationStatusBySection,
                    setValidationStatusBySection
                  )
                }>
                <RPMMiniTable
                  id={data.id}
                  fetchFunction={fetchRpmAccountsByPAsset}
                  removeFunction={deleteLink}
                  dataSubject={getTallyRpmAccountsByPAssetSubject}
                  loadingSubject={getTallyRpmAccountsByPAssetLoadingSubject}
                  showRemove
                />
              </DetailViewValidationCard>
            </div>
            <div className="space-y-6">
              <DetailViewCard
                enableCollapse
                key="asset-detail-card"
                title="Info">
                <div className="min-h-[15rem]">
                  <DetailPanelContainer
                    data={provisionalAssetSectionsInfo(data)}
                    saveAction={(d) => saveAction(d as ProvisionalAssetUpdate)}
                  />
                </div>
              </DetailViewCard>
            </div>
          </div>
        );
      case 1:
        return (
          <Fragment>
            <AttributionTable
              entityType="provisional-asset"
              entityId={id as string}
            />
            <SpiffTable
              entityType="provisional-asset"
              entityId={id as string}
            />
            <CorrectionMiniTable
              id={id as string}
              fetchFunction={fetchCorrections}
              dataSubject={getTallyCorrectionsSubject}
              loadingSubject={getTallyCorrectionsLoadingSubject}
              assetNumber={data.assetNumber || ''}
              supplierName={data.supplierName || ''}
              commissionsStatus={data.commissionStatus || ''}
              type="provisional-asset"
            />
          </Fragment>
        );
      case 2:
        return (
          <AllocationMiniTable
            id={id as string}
            type="combined-assets"
          />
        );
      case 3:
        return (
          <HistoryMiniTable
            id={id as string}
            fetchFunction={fetchChangeHistory}
            dataSubject={getChangeHistorySubject}
            loadingSubject={getChangeHistoryLoadingSubject}
            recordType="provisional_asset"
          />
        );
      default:
        return null;
    }
  };

  const saveAction = (update: ProvisionalAssetUpdate) => updateProvisionalAsset(update);
  const withValidationSection = tabIndex === 0 || tabIndex === 2;

  return (
    <PageLayout
      className="mb-10"
      dataTestId="provisional-asset-detail-page"
      subTitle="Provisional Asset"
      pageTitle={data.assetNumber || ''}
      showLink={false}
      link={link}
      showIcon={true}
      icon="provisionalAssetMed"
      key={data.id}
      showArchived={!data.active}
      showCopy={true}
      copyValue={data.assetNumber || ''}
      pageActions={pageActions()}
      tabs={tabs}
      handleTabChange={handleTabChange}
      activeTab={tabIndex}>
      <div className="grid grid-cols-3 mt-6 space-x-6">
        {tabs.map((tab, index) => (
          <TabPanel
            key={tab}
            value={tabIndex}
            index={index}
            boxPaddingTop={0}
            className={`${withValidationSection ? 'col-span-2' : 'col-span-3'}`}>
            {tabContent()}
          </TabPanel>
        ))}
        {withValidationSection && (
          <div className="space-y-6">
            <DetailViewValidationCard
              enableCollapse
              key="asset-validation-card"
              title="Validation"
              dataTestId="validation-detail-container"
              data={assetValidationInfo(data)}
              saveAction={(d) => saveAction(d as ProvisionalAssetUpdate)}
              detailValidator={(d) =>
                handleRunValidator(
                  'commissionsValidation',
                  d,
                  handleValidationStatusValidation,
                  validationStatusBySection,
                  setValidationStatusBySection
                )
              }
            />
            <TicketMiniTable
              id={data.id}
              fetchFunction={fetchTicketsByPAId}
              association={{
                type: 'provisional_asset',
                body: {
                  entityId: id
                }
              }}
              showCreate={true}
              objectType="provisionalAsset"
              objectData={data}
              objectLinkId={data.id}
              objectLinkDisplay={`Provisional Asset ${data.assetNumber}`}
            />
            <DetailsComments
              id={data.id}
              entityType="provisional_asset"
            />
            <DetailViewCard
              enableCollapse
              key="meta_detail"
              title="Meta">
              <div>
                <DetailPanelContainer
                  dataTestId="meta_detail-container"
                  data={assetSectionMeta(data)}
                  saveAction={(d) => saveAction(d as ProvisionalAssetUpdate)}
                />
              </div>
            </DetailViewCard>
            <NewLinkDialog
              open={isLinkFormOpen}
              setOpen={setIsLinkFormOpen}
              asset={data}
              typeAsset={EntityType.PROVISIONAL_ASSET}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};
